// The closed terminology catalog (spec 02 §2.5). Vertical packs may override
// the tenant-visible label for any of these canonical term keys and no others;
// `terminology_overrides.term_key` is constrained to this set, and the
// packages/verticals cross-reference lint rejects any pack terminology entry
// whose `term_key` is absent here (spec 02 §2.11).
//
// Count reconciliation (PR p1/verticals-seed): spec 02 §2.5 states "61 keys at
// v1" and enumerates exactly 61; this catalog mirrors that list verbatim and
// in order. An earlier coherence audit that reported 61 was correct — there is
// no 60/61 drift to fix. `TERMINOLOGY_KEY_COUNT` + the contracts test pin the
// count so the catalog and the spec can never silently diverge.

import { SUPPORTED_LOCALES, type Locale } from "./locales.js";

/**
 * The 61 canonical term keys, in spec order (02 §2.5). Frozen tuple so it can
 * seed a `z.enum(...)` in the onboarding contract when module 02 lands and so
 * `TermKey` is the exact union.
 */
export const TERMINOLOGY_KEYS = [
  "work_order",
  "work_center",
  "routing_step",
  "stock_unit",
  "lot",
  "remnant",
  "off_cut",
  "waste",
  "scrap",
  "rework",
  "shift",
  "department",
  "facility",
  "machine",
  "operator",
  "quote",
  "sales_order",
  "purchase_order",
  "supplier",
  "customer",
  "bom",
  "product",
  "material",
  "opening_stock",
  "pull_test",
  "quality_check",
  "downtime",
  "setup",
  "teardown",
  "barrel",
  "bobbin",
  "batch",
  "planned_order",
  "promise_date",
  "drawing",
  "twisting",
  "coating",
  "remaining_length",
  "measured_length",
  "claimed_length",
  "calibration_variance",
  "location",
  "warehouse",
  "transfer",
  "cycle_count",
  "defect",
  "hold",
  "release",
  "co_product",
  "by_product",
  "regrind",
  "pellet",
  "extrusion",
  "armoring",
  "sheathing",
  "engraving",
  "cross_section",
  "core_count",
  "voltage_rating",
  "color_code",
  "price_tier",
] as const;

export type TermKey = (typeof TERMINOLOGY_KEYS)[number];

/** Terminology resolves in every supported locale (spec 02 §2.5, D-430). */
export const TERMINOLOGY_LOCALES = SUPPORTED_LOCALES;
export type TermLocale = Locale;

/**
 * Base labels for every canonical term key, in every supported locale (spec 02 §2.5).
 * This is the LOWEST layer of the terminology resolution order — the neutral
 * factory vocabulary a tenant sees when neither a tenant nor a pack override
 * exists for the requested (term_key, locale). The `generic` vertical pack ships
 * an empty terminology bundle, so a generic tenant renders entirely off this map
 * (spec 02 §2.13). A missing pack/tenant locale row falls back HERE for the
 * REQUESTED locale — never to another locale (spec 02 §2.5). Every key carries
 * every locale (i18n parity); the zh-Hant and hi columns are copied from
 * docs/i18n/glossary.json, whose rows for these keys must equal this map; the contracts test pins full
 * coverage + the ≤80-char DB `label` constraint (§2.5).
 */
export const TERMINOLOGY_BASE: Readonly<Record<TermKey, Readonly<Record<TermLocale, string>>>> =
  Object.freeze({
    work_order: {
      en: "Work order",
      ru: "Заказ на производство",
      zh: "工单",
      fr: "Ordre de fabrication",
      "zh-Hant": "工單",
      hi: "वर्क ऑर्डर",
    },
    work_center: {
      en: "Work center",
      ru: "Рабочий центр",
      zh: "工作中心",
      fr: "Poste de charge",
      "zh-Hant": "工作中心",
      hi: "वर्क सेंटर",
    },
    routing_step: {
      en: "Routing step",
      ru: "Операция маршрута",
      zh: "工艺工序",
      fr: "Opération de gamme",
      "zh-Hant": "製程步驟",
      hi: "रूटिंग स्टेप",
    },
    stock_unit: {
      en: "Stock unit",
      ru: "Складская единица",
      zh: "库存单元",
      fr: "Unité de stock",
      "zh-Hant": "庫存單元",
      hi: "स्टॉक यूनिट",
    },
    lot: { en: "Lot", ru: "Партия", zh: "批次", fr: "Lot", "zh-Hant": "批號", hi: "लॉट" },
    remnant: {
      en: "Remnant",
      ru: "Остаток",
      zh: "余料",
      fr: "Chute",
      "zh-Hant": "餘料",
      hi: "रेमनेंट",
    },
    off_cut: {
      en: "Off-cut",
      ru: "Обрезок",
      zh: "边角料",
      fr: "Tronçon",
      "zh-Hant": "短段",
      hi: "कट पीस",
    },
    waste: { en: "Waste", ru: "Отходы", zh: "废料", fr: "Déchet", "zh-Hant": "廢料", hi: "वेस्ट" },
    scrap: { en: "Scrap", ru: "Брак", zh: "报废", fr: "Rebut", "zh-Hant": "報廢", hi: "स्क्रैप" },
    rework: {
      en: "Rework",
      ru: "Доработка",
      zh: "返工",
      fr: "Retouche",
      "zh-Hant": "重工",
      hi: "रीवर्क",
    },
    shift: { en: "Shift", ru: "Смена", zh: "班次", fr: "Équipe", "zh-Hant": "班別", hi: "शिफ्ट" },
    department: {
      en: "Department",
      ru: "Подразделение",
      zh: "部门",
      fr: "Département",
      "zh-Hant": "部門",
      hi: "विभाग",
    },
    facility: {
      en: "Facility",
      ru: "Площадка",
      zh: "工厂",
      fr: "Site",
      "zh-Hant": "廠區",
      hi: "प्लांट",
    },
    machine: {
      en: "Machine",
      ru: "Станок",
      zh: "设备",
      fr: "Machine",
      "zh-Hant": "機台",
      hi: "मशीन",
    },
    operator: {
      en: "Operator",
      ru: "Оператор",
      zh: "操作员",
      fr: "Opérateur",
      "zh-Hant": "作業員",
      hi: "ऑपरेटर",
    },
    quote: {
      en: "Quote",
      ru: "Коммерческое предложение",
      zh: "报价单",
      fr: "Devis",
      "zh-Hant": "報價單",
      hi: "कोटेशन",
    },
    sales_order: {
      en: "Sales order",
      ru: "Заказ на продажу",
      zh: "销售订单",
      fr: "Commande client",
      "zh-Hant": "客戶訂單",
      hi: "सेल्स ऑर्डर",
    },
    purchase_order: {
      en: "Purchase order",
      ru: "Заказ на закупку",
      zh: "采购订单",
      fr: "Commande d'achat",
      "zh-Hant": "採購單",
      hi: "परचेज़ ऑर्डर",
    },
    supplier: {
      en: "Supplier",
      ru: "Поставщик",
      zh: "供应商",
      fr: "Fournisseur",
      "zh-Hant": "供應商",
      hi: "सप्लायर",
    },
    customer: {
      en: "Customer",
      ru: "Клиент",
      zh: "客户",
      fr: "Client",
      "zh-Hant": "客戶",
      hi: "ग्राहक",
    },
    bom: {
      en: "Bill of materials",
      ru: "Спецификация",
      zh: "物料清单",
      fr: "Nomenclature",
      "zh-Hant": "物料清單",
      hi: "बिल ऑफ़ मटीरियल्स",
    },
    product: {
      en: "Product",
      ru: "Продукт",
      zh: "产品",
      fr: "Produit",
      "zh-Hant": "產品",
      hi: "उत्पाद",
    },
    material: {
      en: "Material",
      ru: "Материал",
      zh: "材料",
      fr: "Matière",
      "zh-Hant": "材料",
      hi: "मटीरियल",
    },
    opening_stock: {
      en: "Opening stock",
      ru: "Начальные остатки",
      zh: "期初库存",
      fr: "Stock initial",
      "zh-Hant": "期初庫存",
      hi: "ओपनिंग स्टॉक",
    },
    pull_test: {
      en: "Pull test",
      ru: "Испытание на разрыв",
      zh: "拉力测试",
      fr: "Essai de traction",
      "zh-Hant": "拉力測試",
      hi: "पुल टेस्ट",
    },
    quality_check: {
      en: "Quality check",
      ru: "Контроль качества",
      zh: "质量检查",
      fr: "Contrôle qualité",
      "zh-Hant": "品檢",
      hi: "क्वालिटी चेक",
    },
    downtime: {
      en: "Downtime",
      ru: "Простой",
      zh: "停机",
      fr: "Arrêt",
      "zh-Hant": "停機",
      hi: "डाउनटाइम",
    },
    setup: {
      en: "Setup",
      ru: "Наладка",
      zh: "换型",
      fr: "Réglage",
      "zh-Hant": "調機",
      hi: "सेटअप",
    },
    teardown: {
      en: "Teardown",
      ru: "Разборка",
      zh: "拆卸",
      fr: "Démontage",
      "zh-Hant": "拆機",
      hi: "टियरडाउन",
    },
    barrel: {
      en: "Barrel",
      ru: "Барабан",
      zh: "线桶",
      fr: "Touret",
      "zh-Hant": "線桶",
      hi: "ड्रम",
    },
    bobbin: {
      en: "Bobbin",
      ru: "Бобина",
      zh: "线盘",
      fr: "Bobine",
      "zh-Hant": "線軸",
      hi: "बॉबिन",
    },
    batch: {
      en: "Batch",
      ru: "Серия",
      zh: "批",
      fr: "Lot de production",
      "zh-Hant": "批次",
      hi: "बैच",
    },
    planned_order: {
      en: "Planned order",
      ru: "Плановый заказ",
      zh: "计划订单",
      fr: "Ordre planifié",
      "zh-Hant": "計畫訂單",
      hi: "प्लान्ड ऑर्डर",
    },
    promise_date: {
      en: "Promise date",
      ru: "Дата поставки",
      zh: "承诺交期",
      fr: "Date promise",
      "zh-Hant": "承諾交期",
      hi: "वादा की गई तारीख",
    },
    drawing: {
      en: "Drawing",
      ru: "Волочение",
      zh: "拉丝",
      fr: "Tréfilage",
      "zh-Hant": "伸線",
      hi: "वायर ड्रॉइंग",
    },
    twisting: {
      en: "Twisting",
      ru: "Скрутка",
      zh: "绞合",
      fr: "Toronnage",
      "zh-Hant": "絞線",
      hi: "स्ट्रैंडिंग",
    },
    coating: {
      en: "Coating",
      ru: "Покрытие",
      zh: "涂层",
      fr: "Revêtement",
      "zh-Hant": "被覆",
      hi: "कोटिंग",
    },
    remaining_length: {
      en: "Remaining length",
      ru: "Остаточная длина",
      zh: "剩余长度",
      fr: "Longueur restante",
      "zh-Hant": "剩餘長度",
      hi: "शेष लंबाई",
    },
    measured_length: {
      en: "Measured length",
      ru: "Измеренная длина",
      zh: "实测长度",
      fr: "Longueur mesurée",
      "zh-Hant": "實測長度",
      hi: "मापी गई लंबाई",
    },
    claimed_length: {
      en: "Claimed length",
      ru: "Заявленная длина",
      zh: "报称长度",
      fr: "Longueur déclarée",
      "zh-Hant": "計米長度",
      hi: "काउंटर लंबाई",
    },
    calibration_variance: {
      en: "Calibration variance",
      ru: "Отклонение калибровки",
      zh: "校准偏差",
      fr: "Écart d'étalonnage",
      "zh-Hant": "校正偏差",
      hi: "कैलिब्रेशन विचलन",
    },
    location: {
      en: "Location",
      ru: "Место хранения",
      zh: "库位",
      fr: "Emplacement",
      "zh-Hant": "儲位",
      hi: "लोकेशन",
    },
    warehouse: {
      en: "Warehouse",
      ru: "Склад",
      zh: "仓库",
      fr: "Entrepôt",
      "zh-Hant": "倉庫",
      hi: "गोदाम",
    },
    transfer: {
      en: "Transfer",
      ru: "Перемещение",
      zh: "转移",
      fr: "Transfert",
      "zh-Hant": "調撥",
      hi: "ट्रांसफ़र",
    },
    cycle_count: {
      en: "Cycle count",
      ru: "Циклический пересчёт",
      zh: "循环盘点",
      fr: "Inventaire tournant",
      "zh-Hant": "循環盤點",
      hi: "साइकिल काउंट",
    },
    defect: {
      en: "Defect",
      ru: "Дефект",
      zh: "缺陷",
      fr: "Défaut",
      "zh-Hant": "不良",
      hi: "डिफ़ेक्ट",
    },
    hold: {
      en: "Hold",
      ru: "Блокировка",
      zh: "保留",
      fr: "Blocage",
      "zh-Hant": "凍結",
      hi: "होल्ड",
    },
    release: {
      en: "Release",
      ru: "Разблокировка",
      zh: "放行",
      fr: "Libération",
      "zh-Hant": "放行",
      hi: "रिलीज़",
    },
    co_product: {
      en: "Co-product",
      ru: "Сопутствующий продукт",
      zh: "联产品",
      fr: "Coproduit",
      "zh-Hant": "聯產品",
      hi: "सह-उत्पाद",
    },
    by_product: {
      en: "By-product",
      ru: "Побочный продукт",
      zh: "副产品",
      fr: "Sous-produit",
      "zh-Hant": "副產品",
      hi: "उप-उत्पाद",
    },
    regrind: {
      en: "Regrind",
      ru: "Вторичный материал",
      zh: "再生料",
      fr: "Broyat",
      "zh-Hant": "回收料",
      hi: "रीग्राइंड",
    },
    pellet: {
      en: "Pellet",
      ru: "Гранулят",
      zh: "颗粒",
      fr: "Granulé",
      "zh-Hant": "粒料",
      hi: "पेलेट",
    },
    extrusion: {
      en: "Extrusion",
      ru: "Экструзия",
      zh: "挤出",
      fr: "Extrusion",
      "zh-Hant": "押出",
      hi: "एक्सट्रूज़न",
    },
    armoring: {
      en: "Armoring",
      ru: "Бронирование",
      zh: "铠装",
      fr: "Armurage",
      "zh-Hant": "鎧裝",
      hi: "आर्मरिंग",
    },
    sheathing: {
      en: "Sheathing",
      ru: "Оболочка",
      zh: "护套",
      fr: "Gainage",
      "zh-Hant": "外被",
      hi: "शीथिंग",
    },
    engraving: {
      en: "Engraving",
      ru: "Гравировка",
      zh: "打标",
      fr: "Gravure",
      "zh-Hant": "印字",
      hi: "मार्किंग",
    },
    cross_section: {
      en: "Cross section",
      ru: "Сечение",
      zh: "截面",
      fr: "Section",
      "zh-Hant": "截面積",
      hi: "क्रॉस सेक्शन",
    },
    core_count: {
      en: "Core count",
      ru: "Число жил",
      zh: "芯数",
      fr: "Nombre de conducteurs",
      "zh-Hant": "芯數",
      hi: "कोर की संख्या",
    },
    voltage_rating: {
      en: "Voltage rating",
      ru: "Номинальное напряжение",
      zh: "额定电压",
      fr: "Tension nominale",
      "zh-Hant": "額定電壓",
      hi: "वोल्टेज रेटिंग",
    },
    color_code: {
      en: "Color code",
      ru: "Цветовой код",
      zh: "颜色代码",
      fr: "Code couleur",
      "zh-Hant": "色標",
      hi: "कलर कोड",
    },
    price_tier: {
      en: "Price tier",
      ru: "Ценовой уровень",
      zh: "价格层级",
      fr: "Palier de prix",
      "zh-Hant": "價格等級",
      hi: "प्राइस टियर",
    },
  });

/** Base label for a term in a locale (spec 02 §2.5 lowest resolution layer). */
export function baseTerminologyLabel(termKey: TermKey, locale: TermLocale): string {
  return TERMINOLOGY_BASE[termKey][locale];
}

/** The authoritative count (spec 02 §2.5). Pinned by the contracts test. */
export const TERMINOLOGY_KEY_COUNT = TERMINOLOGY_KEYS.length;

const TERM_KEY_SET: ReadonlySet<string> = new Set(TERMINOLOGY_KEYS);

/** Membership test used by the verticals lint (spec 02 §2.11). */
export function isTermKey(key: string): key is TermKey {
  return TERM_KEY_SET.has(key);
}
